/**
 * Bottlenecks: where records sit right now (open, by how long), how long a
 * stay normally lasts, and a ranked table. Open counts are current state; the
 * window only affects entered/exited/dwell.
 */
import React, { useMemo, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Domain, StageRow, Stages, WindowParams, flowApi } from './flowApi';
import {
  AGING_BUCKETS, UNAGED, C, DOMAINS, DOMAIN_COLOR, DOMAIN_LABEL, DOMAIN_MARKER, axisLine, axisTick, gridProps, STATIC, tooltipProps,
} from './chartTheme';
import {
  ChartCard, DomainName, EmptyNote, Pill, Remote, fmtHours, fmtN, useRemote,
} from './flowUi';
import { DIMMED_OPACITY, HatchPattern, LivingLegend, STAT, STATUS, Segmented } from '../../../components/tide';
import type { LegendItem } from '../../../components/tide';

/** Aged records only: a record with no stage-entry time says nothing about how long it has waited. */
export const bottleneckScore = (s: StageRow) => Math.max(0, s.open - (s.unaged || 0)) * (s.oldestDays ?? 0);

const SCORE_HELP = 'Bottleneck score = open records with a known stage date x days the oldest of them has been in the stage. Big and old ranks first.';
const UNAGED_SHARE_NOTE = 0.25;
const HATCH_ID = 'tide-rf-unaged-hatch';
const AGE_KEYS = [...AGING_BUCKETS.map((b) => b.key), UNAGED.key] as string[];

export default function BottlenecksView({ window: w }: { window: WindowParams }) {
  const { state, retry } = useRemote<Stages>(`stages|${w.from}|${w.to}`, () => flowApi.stages(w));
  return <Remote state={state} retry={retry} skeleton="chart">{(data) => <BottlenecksBody data={data} />}</Remote>;
}

function BottlenecksBody({ data }: { data: Stages }) {
  const [domain, setDomain] = useState<'ALL' | Domain>('ALL');
  const [focusBands, setFocusBands] = useState<string[]>(AGE_KEYS);
  const present = useMemo(() => DOMAINS.filter((d) => data.stages.some((s) => s.domain === d)), [data]);
  const rows = useMemo(() => data.stages.filter((s) => domain === 'ALL' || s.domain === domain), [data, domain]);
  const ranked = useMemo(() => [...rows].sort((a, b) => bottleneckScore(b) - bottleneckScore(a)), [rows]);

  const label = (s: StageRow) => (domain === 'ALL' ? `${DOMAIN_LABEL[s.domain]} · ${s.label}` : s.label);
  const agingData = rows.map((s) => ({ name: label(s), ...s.aging, unaged: s.unaged || 0 }));
  const dwellData = rows
    .filter((s) => s.medianDwellHours != null || s.p90DwellHours != null)
    .map((s) => ({ name: label(s), median: s.medianDwellHours ?? undefined, p90: s.p90DwellHours ?? undefined }));
  const totalOpen = rows.reduce((s, r) => s + r.open, 0);
  const totalUnaged = rows.reduce((s, r) => s + (r.unaged || 0), 0);
  const unagedShare = totalOpen > 0 ? totalUnaged / totalOpen : 0;

  // open = sum(aging) + unaged (contract), so each band's share of open is a real whole.
  const bandItems: LegendItem[] = [
    ...AGING_BUCKETS.map((b) => ({
      key: b.key, label: b.label, color: b.color, marker: 'square' as const,
      value: rows.reduce((s, r) => s + (r.aging[b.key as keyof StageRow['aging']] || 0), 0),
      unit: 'open', share: { of: totalOpen, label: 'open' },
    })),
    {
      key: UNAGED.key, label: UNAGED.label, color: UNAGED.color, marker: UNAGED.marker, description: `Grey and hatched: ${UNAGED.note}.`,
      value: totalUnaged, unit: 'open', share: { of: totalOpen, label: 'open' },
    },
  ];
  const isolated = focusBands.length < AGE_KEYS.length;
  const opacityFor = (key: string) => (!isolated || focusBands.includes(key) ? 1 : DIMMED_OPACITY);

  const domainOptions = [
    { value: 'ALL', label: 'All domains' },
    ...present.map((d) => ({ value: d, label: DOMAIN_LABEL[d], marker: { shape: DOMAIN_MARKER[d], color: DOMAIN_COLOR[d] } })),
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
        <Segmented label="Domain" options={domainOptions} value={domain} onChange={(v) => setDomain(v as 'ALL' | Domain)} />
        <p className="tide-small tide-ink-2" style={{ margin: 0, paddingBottom: 8 }}>Open counts are right now. The window sets entered, exited and dwell.</p>
      </div>

      {/* Only reachable with no stages at all: the Domain control offers only domains that have stages. */}
      {rows.length === 0 ? <EmptyNote reason="none">No stages were reported, so there is no open work to rank.</EmptyNote> : (
        <>
          {unagedShare >= UNAGED_SHARE_NOTE && (
            <p
              role="note"
              data-testid="unaged-note"
              className="tide-small"
              style={{ margin: 0, padding: '8px 12px', borderRadius: 6, background: STATUS.caution.bg, color: STATUS.caution.fg, borderLeft: `4px solid ${STATUS.caution.fg}`, fontWeight: 500 }}
            >
              {fmtN(totalUnaged)} of {fmtN(totalOpen)} open records ({Math.round(unagedShare * 100)}%) have {UNAGED.note}, so their age is unknown. They are shown grey and left out of the scores and flags.
            </p>
          )}
          <ChartCard
            side
            title="Open records by stage and age"
            hint={`Time in the current stage. Redder is older. Grey means ${UNAGED.note}.`}
            summary={`Open records by stage and aging bucket, ${fmtN(totalOpen)} open in total. ${rows.map((s) => `${label(s)}: ${s.open} open`).join('; ')}.`}
            legend={(
              <LivingLegend
                stacked
                density="compact"
                title="Time in stage"
                mode="isolate"
                items={bandItems}
                selected={focusBands}
                onChange={setFocusBands}
                scope="Choose a band to highlight it in every stage; choose it again to restore. Highlights marks only."
              />
            )}
          >
            <ResponsiveContainer width="100%" height="100%" minHeight={Math.max(280, rows.length * 30 + 70)}>
              <BarChart data={agingData} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                <defs><HatchPattern id={HATCH_ID} color={UNAGED.color} /></defs>
                <CartesianGrid {...gridProps} horizontal={false} />
                <XAxis type="number" tick={axisTick} axisLine={axisLine} tickLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={axisTick} axisLine={false} tickLine={false} width={170} interval={0} />
                <Tooltip {...tooltipProps} formatter={(v: number, name: string) => [fmtN(v), name === UNAGED.label ? `${name} (${UNAGED.note})` : name]} />
                {AGING_BUCKETS.map((b) => (
                  <Bar key={b.key} dataKey={b.key} name={b.label} stackId="age" fill={b.color} fillOpacity={opacityFor(b.key)} stroke="var(--tide-surface)" strokeWidth={1} {...STATIC} />
                ))}
                <Bar dataKey={UNAGED.key} name={UNAGED.label} stackId="age" fill={`url(#${HATCH_ID})`} fillOpacity={opacityFor(UNAGED.key)} stroke={UNAGED.color} strokeWidth={1} {...STATIC} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            side
            title="Median vs p90 dwell time"
            hint="Completed stays that ended in the window. A wide gap means a long tail."
            summary={`Dwell time per stage. ${dwellData.map((d) => `${d.name}: median ${fmtHours(d.median)}, p90 ${fmtHours(d.p90)}`).join('; ') || 'No completed stays in this window.'}`}
            legend={dwellData.length === 0 ? undefined : (
              <LivingLegend
                stacked
                density="compact"
                title="Measures"
                items={[
                  { key: 'median', label: STAT.median.label, color: C.median, marker: 'square', description: 'Half of completed stays were shorter than this (bars).' },
                  { key: 'p90', label: STAT.p90.label, color: C.tail, marker: 'circle', description: '9 in 10 completed stays were shorter than this (line).' },
                ]}
              />
            )}
          >
            {dwellData.length === 0 ? <EmptyNote>No stays completed in this window.</EmptyNote> : (
              <ResponsiveContainer width="100%" height="100%" minHeight={300}>
                <ComposedChart data={dwellData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid {...gridProps} vertical={false} />
                  <XAxis dataKey="name" tick={axisTick} axisLine={axisLine} tickLine={false} interval={0} angle={-30} textAnchor="end" height={80} />
                  <YAxis tick={axisTick} axisLine={false} tickLine={false} width={48} unit=" h" />
                  <Tooltip {...tooltipProps} formatter={(v: number, name: string) => [fmtHours(v), name]} />
                  <Bar dataKey="median" name="Median" fill={C.median} radius={[4, 4, 0, 0]} {...STATIC} />
                  <Line dataKey="p90" name="p90" type="monotone" stroke={C.tail} strokeWidth={2} dot={{ r: 3.5, fill: C.tail }} connectNulls={false} {...STATIC} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          <section className="tide-panel">
            <header className="tide-panel__head">
              <h2 className="tide-panel__title">Stages ranked by bottleneck score</h2>
              <p className="tide-panel__hint">{SCORE_HELP}</p>
            </header>
            <div className="tide-scroll" tabIndex={0} role="region" aria-label="Stages ranked by bottleneck score (scrolls sideways)">
              <table className="tide-table min-w-[900px]">
                <thead>
                  <tr>
                    <th scope="col">Domain</th>
                    <th scope="col">Stage</th>
                    <th scope="col" className="tide-num" title={SCORE_HELP}>Open</th>
                    <th scope="col" className="tide-num">Unowned</th>
                    <th scope="col" className="tide-num" title={UNAGED.note}>Age unknown</th>
                    <th scope="col" className="tide-num">Entered</th>
                    <th scope="col" className="tide-num">Exited</th>
                    <th scope="col" className="tide-num">Median dwell</th>
                    <th scope="col" className="tide-num">p90 dwell</th>
                    <th scope="col" className="tide-num" title={SCORE_HELP}>Oldest</th>
                    <th scope="col">Flag</th>
                  </tr>
                </thead>
                <tbody>
                  {ranked.map((s) => {
                    const aging = (s.oldestDays ?? 0) > 14;
                    const filling = s.exited < s.entered;
                    return (
                      <tr key={`${s.domain}:${s.stage}`}>
                        <td><DomainName domain={s.domain} /></td>
                        <td style={{ fontWeight: 600 }}>{s.label}</td>
                        <td className="tide-num tabular-nums">{fmtN(s.open)}</td>
                        <td className="tide-num tabular-nums">{fmtN(s.unowned)}</td>
                        <td className="tide-num tabular-nums" title={UNAGED.note}>{fmtN(s.unaged || 0)}</td>
                        <td className="tide-num tabular-nums">{fmtN(s.entered)}</td>
                        <td className="tide-num tabular-nums">{fmtN(s.exited)}</td>
                        <td className="tide-num tabular-nums">{fmtHours(s.medianDwellHours)}</td>
                        <td className="tide-num tabular-nums">{fmtHours(s.p90DwellHours)}</td>
                        <td className="tide-num tabular-nums">{s.oldestDays == null ? '-' : `${s.oldestDays} d`}</td>
                        <td>
                          <span className="flex flex-wrap gap-1">
                            {aging && <Pill tone="danger" title="The oldest record here has waited more than 14 days">Oldest over 14 d</Pill>}
                            {filling && <Pill tone="caution" title="More records entered this stage than left it in the window">Filling up</Pill>}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
